import { FormFieldWithInfo } from '@openx/components-core/lib/FormFieldWithInfo/FormFieldWithInfo.tsx';
import { RHFTextFieldWithAdornment } from '@openx/components-core/lib/TextField/RHFTextFieldWithAdornment.tsx';
import type { DealFormData } from '@openx/types/deal.ts';
import { useCallback } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { grossFeeTooltip } from '../../../../../components/CuratedDealFees/constants.ts';
import { useIsCpmRevenue } from '../../hooks/useIsCpmRevenue.ts';

type GrossFeeProps = {
  currency: string;
  index: number;
};

export const GrossFee = ({ currency, index }: GrossFeeProps) => {
  const isCpmRevenue = useIsCpmRevenue(index);
  const { t } = useTranslation();
  const { trigger } = useFormContext<DealFormData>();

  const name: Path<DealFormData> = isCpmRevenue
    ? `third_party_fees_config.${index}.gross_cpm_cap`
    : `third_party_fees_config.${index}.gross_share`;

  const handleChangeComplete = useCallback(() => {
    // Trigger 94% validation on the gross_share field (which displays the error)
    // Only relevant when in PoM mode
    if (!isCpmRevenue) {
      trigger('third_party_fees_config.0.gross_share');
    }
  }, [isCpmRevenue, trigger]);

  return (
    <FormFieldWithInfo
      key={isCpmRevenue.toString()}
      info={isCpmRevenue ? t(grossFeeTooltip.cpm) : t(grossFeeTooltip.pom)}
      tooltipTop="30px"
      dataTest={isCpmRevenue ? 'fixed-cpm' : 'percent-of-media'}
    >
      <RHFTextFieldWithAdornment
        dataTest={isCpmRevenue ? 'fixed-cpm' : 'percent-of-media'}
        endAdornment={isCpmRevenue ? undefined : '%'}
        formControlProps={{ margin: 'dense' }}
        label={isCpmRevenue ? t('Fixed CPM') : t('Percent of Media')}
        name={name}
        placeholder={isCpmRevenue ? '0.00' : '0.0'}
        startAdornment={isCpmRevenue ? currency : undefined}
        onChangeComplete={handleChangeComplete}
      />
    </FormFieldWithInfo>
  );
};
